import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../../api/client'
import { useStores } from '../../app/StoreContext'
import type { Delivery, DeliveryDashboard, ProposalDashboard } from '../../api/types'
import { Card, Empty, ErrorBox, Num, Spinner, Badge, formatDate, plural } from '../../ui/primitives'
import { GridHead, GridSummary, useGrid, type ColumnDef } from '../../ui/grid'
import { QuickDates } from './QuickDates'
import { RecipientPanel } from './RecipientPanel'

/**
 * Indicatori di sintesi su proposte e consegne.
 *
 * Una nota che vale la pena tenere a mente leggendo la puntualità: nel sistema precedente
 * l'esito consegna aveva "non consegnato" come valore predefinito su centinaia di migliaia di
 * righe mai compilate, e la percentuale che ne usciva era sistematicamente pessimistica.
 * Qui la puntualità è calcolata solo sulle consegne con esito effettivamente dichiarato,
 * e il numero di quelle dichiarate è mostrato accanto: senza, la percentuale non è leggibile.
 */
export function DashboardPage() {
  const { selected, storeFilter, storesReady, storeName } = useStores()
  const navigate = useNavigate()

  const [proposals, setProposals] = useState<ProposalDashboard>()
  const [deliveries, setDeliveries] = useState<DeliveryDashboard>()
  const [loadingProposals, setLoadingProposals] = useState(true)
  const [loadingDeliveries, setLoadingDeliveries] = useState(true)
  const [proposalsError, setProposalsError] = useState<unknown>()
  const [deliveriesError, setDeliveriesError] = useState<unknown>()

  // Date e filtri vivono anche nell'URL (chiavi: da, a, stati, fasce, doc): chi apre una
  // consegna dall'elenco e torna indietro ritrova la situazione che aveva lasciato, e un
  // indirizzo copiato porta con sé i filtri.
  const [searchParams, setSearchParams] = useSearchParams()

  // Finestra temporale delle consegne. Vuota = la finestra che il server usa da sé (±30 giorni),
  // che è anche tutto ciò che lo specchio conosce: per questo la scorciatoia si chiama
  // «Tutto il periodo» e non «Sempre».
  const [from, setFrom] = useState(searchParams.get('da') ?? '')
  const [to, setTo] = useState(searchParams.get('a') ?? '')

  // L'elenco dietro i numeri: le stesse consegne del riquadro, riga per riga.
  const [list, setList] = useState<Delivery[]>()
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState<unknown>()

  // Filtri accesi cliccando avanzamento, scostamento e «documento mancante». Dentro lo stesso
  // riquadro le voci si sommano (Completata + Spedizione annunciata = l'una o l'altra); fra
  // riquadri diversi si intersecano. Ogni voce si spegne col secondo click.
  const [statusFilters, setStatusFilters] = useState<string[]>(
    () => searchParams.get('stati')?.split(',').filter(Boolean) ?? [],
  )
  const [agingFilters, setAgingFilters] = useState<number[]>(
    () => searchParams.get('fasce')?.split(',').map(Number).filter((n) => !Number.isNaN(n)) ?? [],
  )
  const [docFilter, setDocFilter] = useState(searchParams.get('doc') === '1')

  const toggle = <T,>(values: T[], value: T) =>
    values.includes(value) ? values.filter((v) => v !== value) : [...values, value]

  // Lo stato si riscrive nell'URL a ogni cambio, SENZA aggiungere voci alla cronologia
  // (replace): il «indietro» deve saltare dal dettaglio alla dashboard, non ripercorrere
  // ogni click sui filtri. Le chiavi delle proposte (pda, pa, dest) sono di RecipientPanel.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev)
        const setOrDel = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k))
        setOrDel('da', from)
        setOrDel('a', to)
        setOrDel('stati', statusFilters.join(','))
        setOrDel('fasce', agingFilters.join(','))
        setOrDel('doc', docFilter ? '1' : '')
        return p
      },
      { replace: true },
    )
  }, [from, to, statusFilters, agingFilters, docFilter, setSearchParams])

  // Ogni scheda carica per conto suo. Il quadro delle consegne interroga tutte le consegne del
  // periodo e su molti punti vendita arriva a decine di secondi: legandole insieme, la pagina
  // resterebbe bianca fino all'ultima risposta anche se le proposte sono pronte in un attimo.
  // Finché l'elenco dei punti vendita non è arrivato il filtro non è affidabile.
  const loadProposals = useCallback(() => {
    if (!storesReady) return
    setLoadingProposals(true)
    setProposalsError(undefined)
    api
      .get<ProposalDashboard>('/dashboard/proposals', { storeCode: storeFilter })
      .then(setProposals)
      .catch(setProposalsError)
      .finally(() => setLoadingProposals(false))
  }, [storeFilter, storesReady])

  const loadDeliveries = useCallback(() => {
    if (!storesReady) return
    setLoadingDeliveries(true)
    setDeliveriesError(undefined)
    api
      .get<DeliveryDashboard>('/dashboard/deliveries', {
        storeCode: storeFilter,
        from: from || undefined,
        to: to || undefined,
      })
      .then(setDeliveries)
      .catch(setDeliveriesError)
      .finally(() => setLoadingDeliveries(false))

    setListLoading(true)
    setListError(undefined)
    api
      .get<Delivery[]>('/deliveries', {
        storeCode: storeFilter,
        from: from || undefined,
        to: to || undefined,
        includeCompleted: true,
      })
      .then(setList)
      .catch(setListError)
      .finally(() => setListLoading(false))
  }, [storeFilter, storesReady, from, to])

  useEffect(loadProposals, [loadProposals])
  useEffect(loadDeliveries, [loadDeliveries])

  const punctuality =
    deliveries && deliveries.declaredOutcomes > 0
      ? Math.round((deliveries.onTimeOutcomes / deliveries.declaredOutcomes) * 100)
      : undefined

  // --- L'elenco filtrato dai click sui riquadri --------------------------------------------

  const filtered = useMemo(() => {
    let rows = list ?? []
    if (statusFilters.length > 0) rows = rows.filter((d) => statusFilters.includes(d.status))
    if (agingFilters.length > 0) {
      rows = rows.filter((d) => {
        const bucket = agingBucket(d)
        return bucket !== undefined && agingFilters.includes(bucket)
      })
    }
    if (docFilter) rows = rows.filter((d) => d.documentStatus === 'missing' && d.status !== 'completed')
    return rows
  }, [list, statusFilters, agingFilters, docFilter])

  const columns = useMemo<ColumnDef<Delivery>[]>(
    () => [
      { key: 'store', label: 'Punto vendita', value: (d) => `${d.storeCode} ${storeName(d.storeCode) ?? ''}` },
      { key: 'supplier', label: 'Fornitore', value: (d) => `${d.supplierName} ${d.supplierCode}` },
      { key: 'origin', label: 'Origine', value: (d) => (d.origin === 'cedi' ? 'CEDI' : 'fornitore') },
      { key: 'order', label: 'Ordine', value: (d) => d.orderNumber ?? d.documentNumber ?? '' },
      { key: 'expected', label: 'Data attesa', value: (d) => d.effectiveDate ?? null },
      { key: 'status', label: 'Stato', value: (d) => statusLabel(d.status) },
      { key: 'doc', label: 'Documento', value: (d) => documentLabel(d.documentStatus) },
      {
        key: 'late',
        label: 'Ritardo',
        value: (d) => (d.isOverdue ? 'in ritardo' : ''),
        title: 'Filtra scrivendo «ritardo».',
      },
    ],
    [storeName],
  )
  const grid = useGrid(filtered, columns)

  const activeFilters = statusFilters.length + agingFilters.length + (docFilter ? 1 : 0)

  // Le righe a video hanno un tetto: con tutti i punti vendita e tutto il periodo l'elenco
  // supera le decine di migliaia, e un DOM così non serve a nessuno. Il conteggio vero è detto.
  const MOSTRATE = 500

  return (
    <>
      <div className="page-head">
        <h1>Dashboard</h1>
        <div className="page-tools">
          <span className="muted">
            {selected.length === 0 ?
              'Tutti i punti vendita'
            : plural(selected.length, 'punto vendita', 'punti vendita')}
          </span>
        </div>
      </div>

      <Card title="Proposte d'ordine">
        {loadingProposals ?
          <Spinner />
        : proposalsError ?
          <ErrorBox error={proposalsError} onRetry={loadProposals} />
        : <>
        <div className="grid grid-4">
          <Stat label="Aperte" value={<Num value={proposals?.openCount} />} />
          <Stat label="Righe da lavorare" value={<Num value={proposals?.openLines} />} />
          <Stat label="Confermate" value={<Num value={proposals?.confirmedCount} />} />
          <Stat label="Righe confermate" value={<Num value={proposals?.confirmedLines} />} />
        </div>

        {/* Qui c'era una classifica «fornitori principali». Diceva una cosa falsa: le proposte
            verso un centro di distribuzione non hanno un fornitore, e portano tutte il codice
            convenzionale 000000, per cui la tabella elencava «000000 CEDI NORD» e «000000
            MAGAZZINO CASTEL ROMANO» come se fossero due fornitori omonimi. La domanda a cui
            serviva a rispondere — dove è rimasto lavoro — la risolve l'istogramma qui sotto,
            che distingue fornitori e centri di distribuzione e si apre sul dettaglio. */}

        {proposals?.openCount === 0 && <Empty>Nessuna proposta aperta per i punti vendita selezionati.</Empty>}
          </>
        }
      </Card>

      <RecipientPanel />

      <Card title="Consegne">
        <div className="grid filter-grid">
          <div className="field">
            <label>Da</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="field">
            <label>A</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="field field-actions">
            <QuickDates
              from={from}
              to={to}
              allLabel="Tutto il periodo"
              onChange={(f, t) => {
                setFrom(f)
                setTo(t)
              }}
            />
          </div>
        </div>

        {loadingDeliveries ?
          <Spinner label="Caricamento consegne…" />
        : deliveriesError ?
          <ErrorBox error={deliveriesError} onRetry={loadDeliveries} />
        : <>
        <div className="grid grid-4">
          <Stat label="Totale nel periodo" value={<Num value={deliveries?.total} />} />
          <Stat
            label="In ritardo"
            value={<Num value={deliveries?.overdue} />}
            tone={deliveries && deliveries.overdue > 0 ? 'danger' : undefined}
          />
          <Stat
            label="Documento mancante"
            value={<Num value={deliveries?.missingDocument} />}
            active={docFilter}
            onClick={() => setDocFilter((v) => !v)}
            title="Filtra l'elenco sulle consegne senza documento"
          />
          <Stat label="Da controllare" value={<Num value={deliveries?.awaitingReview} />} />
        </div>

        <div className="grid grid-2" style={{ marginTop: 16 }}>
          <div>
            <h3 style={{ marginBottom: 8 }}>Avanzamento</h3>
            <p className="muted" style={{ margin: '0 0 6px' }}>
              Ogni click accende (o spegne) una voce: più voci insieme si sommano nell'elenco.
            </p>
            {deliveries && (
              <table>
                <tbody>
                  {Object.entries(deliveries.byStatus).map(([status, count]) => (
                    <tr
                      key={status}
                      className={`clickable${statusFilters.includes(status) ? ' is-on' : ''}`}
                      onClick={() => setStatusFilters((s) => toggle(s, status))}
                    >
                      <td>
                        <Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>
                      </td>
                      <td className="num">
                        <Num value={count} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h3 style={{ marginBottom: 8 }}>Puntualità</h3>
            {punctuality === undefined ? (
              <Empty>
                Nessuna consegna con esito dichiarato nel periodo: la puntualità non è calcolabile.
              </Empty>
            ) : (
              <>
                <div className="stat-value">{punctuality}%</div>
                <div className="stat-note">
                  su <Num value={deliveries?.declaredOutcomes} /> consegne con esito dichiarato
                  {deliveries && deliveries.total > deliveries.declaredOutcomes && (
                    <>
                      {' '}(le altre <Num value={deliveries.total - deliveries.declaredOutcomes} /> non hanno
                      un esito registrato e non entrano nel calcolo)
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {deliveries && deliveries.aging.some((a) => a.count > 0) && (
          <div style={{ marginTop: 16 }}>
            <h3 style={{ marginBottom: 8 }}>Scostamento dalla data attesa</h3>
            <p className="muted" style={{ margin: '0 0 6px' }}>
              Anche le fasce filtrano l'elenco, e più fasce si sommano: contano le consegne non concluse.
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Fascia</th>
                    <th className="num">Consegne</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.aging
                    .filter((a) => a.count > 0)
                    .map((a) => (
                      <tr
                        key={a.daysThreshold}
                        className={`clickable${agingFilters.includes(a.daysThreshold) ? ' is-on' : ''}`}
                        onClick={() => setAgingFilters((v) => toggle(v, a.daysThreshold))}
                      >
                        <td>{agingLabel(a.daysThreshold)}</td>
                        <td className="num">
                          <Num value={a.count} />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <h3 style={{ marginBottom: 8 }}>
            Le consegne dell'elenco
            {activeFilters > 0 && (
              <>
                {' '}
                <Badge tone="info">{plural(activeFilters, 'filtro attivo', 'filtri attivi')}</Badge>{' '}
                <button
                  type="button"
                  className="grid-clear"
                  onClick={() => {
                    setStatusFilters([])
                    setAgingFilters([])
                    setDocFilter(false)
                  }}
                >
                  Togli i filtri dei riquadri
                </button>
              </>
            )}
          </h3>
          {listLoading ?
            <Spinner label="Caricamento dell'elenco…" />
          : listError ?
            <ErrorBox error={listError} onRetry={loadDeliveries} />
          : filtered.length === 0 ?
            <Empty>Nessuna consegna corrisponde ai filtri.</Empty>
          : <div className="table-wrap">
              <table className="dense">
                <GridHead columns={columns} grid={grid} />
                <tbody>
                  {grid.rows.slice(0, MOSTRATE).map((d) => (
                    <tr
                      key={d.id}
                      className="clickable"
                      onClick={() => navigate(`/planning/${encodeURIComponent(d.id)}`)}
                    >
                      <td>
                        <span className="num">{d.storeCode}</span>{' '}
                        <span className="muted">{storeName(d.storeCode)}</span>
                      </td>
                      <td>
                        {d.supplierName} <span className="muted">({d.supplierCode})</span>
                      </td>
                      <td>{d.origin === 'cedi' ? 'CEDI' : 'fornitore'}</td>
                      <td className="num">{d.orderNumber ?? d.documentNumber ?? '—'}</td>
                      <td>{d.effectiveDate ? formatDate(d.effectiveDate) : '—'}</td>
                      <td>
                        <Badge tone={statusTone(d.status)}>{statusLabel(d.status)}</Badge>
                      </td>
                      <td>{documentLabel(d.documentStatus)}</td>
                      <td>{d.isOverdue ? <Badge tone="danger">In ritardo</Badge> : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <GridSummary grid={grid} total={filtered.length} />
              {grid.rows.length > MOSTRATE && (
                <p className="muted">
                  Mostrate le prime {MOSTRATE} di <Num value={grid.rows.length} /> consegne:
                  restringi con le date, i riquadri o i filtri di colonna.
                </p>
              )}
            </div>
          }
        </div>
          </>
        }
      </Card>
    </>
  )
}

function Stat({
  label,
  value,
  tone,
  onClick,
  active,
  title,
}: {
  label: string
  value: React.ReactNode
  tone?: 'danger'
  onClick?: () => void
  active?: boolean
  title?: string
}) {
  const inner = (
    <>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={tone === 'danger' ? { color: 'var(--danger)' } : undefined}>
        {value}
      </div>
    </>
  )

  // Un riquadro cliccabile è un filtro: da tastiera deve essere un bottone, non un div sordo.
  if (onClick)
    return (
      <button type="button" className={`stat stat-button${active ? ' is-on' : ''}`} onClick={onClick} title={title}>
        {inner}
      </button>
    )

  return <div className="stat">{inner}</div>
}

const AGING_THRESHOLDS = [-30, -14, -7, -3, 0, 3, 7, 14, 30]

/** Numero del giorno, per differenze in giorni: le date ISO senza ora sono mezzanotte UTC. */
function dayNumber(iso: string) {
  return Math.floor(Date.parse(iso) / 86400000)
}

/**
 * La fascia di scostamento di una consegna, con la stessa regola del server: prima soglia che
 * contiene lo scarto, le concluse e quelle senza data fuori dal conto.
 */
function agingBucket(d: Delivery): number | undefined {
  if (d.status === 'completed' || !d.effectiveDate) return undefined
  const oggi = new Date()
  const oggiIso = `${oggi.getFullYear()}-${String(oggi.getMonth() + 1).padStart(2, '0')}-${String(oggi.getDate()).padStart(2, '0')}`
  const giorni = dayNumber(d.effectiveDate) - dayNumber(oggiIso)
  return AGING_THRESHOLDS.find((t) => giorni <= t) ?? AGING_THRESHOLDS[AGING_THRESHOLDS.length - 1]
}

function statusLabel(status: string) {
  switch (status) {
    case 'ordered':
      return 'Ordine confermato'
    case 'shipmentNotified':
      return 'Spedizione annunciata'
    case 'documentReceived':
      return 'Documento ricevuto'
    case 'completed':
      return 'Completata'
    default:
      return status
  }
}

function statusTone(status: string) {
  switch (status) {
    case 'shipmentNotified':
      return 'info' as const
    case 'documentReceived':
      return 'warn' as const
    case 'completed':
      return 'ok' as const
    default:
      return 'neutral' as const
  }
}

function documentLabel(status: string) {
  switch (status) {
    case 'missing':
      return 'Mancante'
    case 'attached':
      return 'Allegato'
    case 'rejected':
      return 'Respinto'
    case 'cedi':
      return 'CEDI'
    case 'shipmentAdvice':
      return 'Da avviso'
    default:
      return status
  }
}

function agingLabel(threshold: number) {
  if (threshold < 0) return `Oltre ${Math.abs(threshold)} giorni di ritardo`
  if (threshold === 0) return 'In scadenza oggi'
  return `Entro ${threshold} giorni`
}
